import { useState } from "react";
import axios from "axios";
import { Camera, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Photo upload dialog used by both depot and tower detail pages.
 * `endpoint` is the POST URL relative to the API root — e.g. `depots/{id}/photos`
 * or `towers/{id}/photos`.
 */
export function PhotoUploadDialog({ subjectId, subjectKind, subjectName, currentUser, onUploaded, buttonClassName, buttonLabel = "Add Photo" }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [data, setData] = useState({ image: null, caption: "", uploadedBy: "", contributorEmail: "" });

  const path = subjectKind === "tower" ? `towers/${subjectId}/photos` : `depots/${subjectId}/photos`;

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be less than 5MB");
    const r = new FileReader();
    r.onload = (ev) => {
      setData((d) => ({ ...d, image: ev.target.result }));
      setPreview(ev.target.result);
    };
    r.readAsDataURL(file);
  };

  const submit = async () => {
    if (!data.image) return toast.error("Please select an image");
    if (!currentUser) {
      if (!data.uploadedBy.trim() || !data.contributorEmail.trim() || !data.contributorEmail.includes("@"))
        return toast.error("Please tell us your name and email so we can credit you.");
    }
    try {
      setUploading(true);
      await axios.post(
        `${API}/${path}`,
        {
          image_data: data.image,
          caption: data.caption || null,
          uploaded_by: data.uploadedBy || null,
          contributor_email: data.contributorEmail || null,
        },
        { withCredentials: true }
      );
      toast.success("Photo uploaded");
      setOpen(false);
      setPreview(null);
      setData({ image: null, caption: "", uploadedBy: "", contributorEmail: "" });
      onUploaded?.();
    } catch (e) {
      if (e.response?.status === 413) toast.error("Image too large — please use a photo under 4.5 MB");
      else toast.error(e.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        className={buttonClassName || "bg-amber-500 hover:bg-amber-600 text-black"}
        onClick={() => setOpen(true)}
        data-testid={`${subjectKind}-upload-photo-btn`}
      >
        <Upload className="w-4 h-4 mr-2" />
        {buttonLabel}
      </Button>
      <DialogContent className="sm:max-w-md bg-[#1a1a1a] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white">Upload Photo</DialogTitle>
          <DialogDescription className="text-white/60">Share a photo of {subjectName}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {preview ? (
            <div className="relative">
              <img src={preview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
              <Button variant="destructive" size="icon" className="absolute top-2 right-2"
                onClick={() => { setPreview(null); setData((d) => ({ ...d, image: null })); }}
              ><X className="w-4 h-4" /></Button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
              onClick={() => document.getElementById(`${subjectKind}-photo-input`)?.click()}
            >
              <Camera className="w-10 h-10 mx-auto mb-3 text-white/20" />
              <p className="text-sm text-white/70">Click to select an image</p>
              <p className="text-xs text-white/50 mt-1">Max 5MB, JPG or PNG</p>
            </div>
          )}
          <input id={`${subjectKind}-photo-input`} type="file" accept="image/*" className="hidden" onChange={onFile} />
          <div>
            <label className="text-sm font-medium mb-2 block text-white/85">Caption (optional)</label>
            <Textarea placeholder="Describe this photo..." value={data.caption}
              onChange={(e) => setData((d) => ({ ...d, caption: e.target.value }))}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
          </div>
          {currentUser ? (
            <p className="text-xs text-white/70">
              Uploading as <span className="text-white/90 font-medium">{currentUser.name || currentUser.email}</span>
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <label className="text-sm font-medium mb-2 block text-white/85">Your name *</label>
                <Input placeholder="Jane Doe" value={data.uploadedBy}
                  onChange={(e) => setData((d) => ({ ...d, uploadedBy: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block text-white/85">Your email *</label>
                <Input type="email" placeholder="never shown publicly" value={data.contributorEmail}
                  onChange={(e) => setData((d) => ({ ...d, contributorEmail: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}
            className="bg-white/[0.06] border-white/20 text-white hover:bg-white/[0.14] hover:!text-white hover:border-white/30">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!data.image || uploading} className="bg-amber-500 hover:bg-amber-600 text-black">
            {uploading ? "Uploading..." : "Upload Photo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
